import clsx from 'clsx'
import { originalLabel } from './helpers/originalLabel'
import Check from '../icons/Check'
import { useRef, useState } from 'react'

type Props = {
  image: Image
  original?: boolean
  selected?: boolean
  displayName: boolean
  chooseImage?: (image: Image) => void
  openVersions?: (image: Image) => void
  thumbnail?: boolean
  version?: boolean
  versionSelected?: boolean
}

export default function RasterImage({
  image,
  original,
  selected,
  displayName,
  chooseImage,
  openVersions,
  thumbnail,
  version,
  versionSelected,
}: Props) {
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  // Check if it's a video by testing if both height and width are null
  const isVideo = image.height === null && image.width === null

  const toggleVideoPlay = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleAssetClick = () => {
    if (!thumbnail && chooseImage) {
      chooseImage(image)
    }
  }

  return (
    <div className="relative flex flex-col gap-1.5 group">
      {chooseImage && (
        <button
          type="button"
          onClick={handleAssetClick}
          key={image.id}
          className={clsx('relative', thumbnail ? 'cursor-move' : 'overflow-hidden', {
            'h-full': version,
          })}
        >
          {thumbnail && <div className="remove-overlay" />}

          {/* Video or Image */}
          {isVideo ? (
            <div className={clsx('relative', thumbnail ? 'w-36 h-36' : 'w-full')}>
              <video
                ref={videoRef}
                src={image.url}
                poster={image.thumbUrl}
                className={clsx(
                  'rounded transition-transform duration-300 ease-in-out select-none bg-gray-100 object-cover',
                  thumbnail ? 'h-36 w-36' : 'w-full group-hover:scale-[1.02]',
                  selected ? 'border-red-400 border-4' : 'border-white',
                  { 'h-full': version },
                )}
                style={{ aspectRatio: '243/162' }}
              />
              {/* Play button in center only */}
              <button
                type="button"
                onClick={toggleVideoPlay}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center bg-black/50 text-white rounded-full h-12 w-12 z-20"
              >
                <span className="text-xl">{isPlaying ? '❚❚' : '▶'}</span>
              </button>
            </div>
          ) : (
            <img
              src={image.thumbUrl}
              className={clsx(
                'rounded transition-transform duration-300 ease-in-out select-none pointer-events-none bg-gray-100 object-cover',
                thumbnail ? 'h-36 w-36' : 'w-full group-hover:scale-[1.02]',
                selected ? 'border-red-400 border-4' : 'border-white',
                { 'h-full': version },
              )}
            />
          )}

          {/* Show count if image has versions */}
          {!thumbnail && openVersions && image?.views && image?.views?.length > 0 && (
            <button
              type="button"
              className="cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                openVersions(image)
              }}
            >
              <div className={clsx('versions', versionSelected ? 'border-2 border-primary' : '')}>
                +{image.views.length}
                {versionSelected && (
                  <span className="h-4 w-4 p-0.5 bg-primary rounded-full flex justify-center items-center">
                    <Check />
                  </span>
                )}
              </div>
            </button>
          )}

          <span className={clsx('checkmark', selected && !thumbnail ? 'scale-100 opacity-100' : 'scale-90 opacity-0')}>
            <Check />
          </span>

          {/* Show dimensions for images or video label for videos */}
          <span className="absolute bottom-2 left-2 text-xs bg-gray-900/80 px-2.5 py-1 rounded-md font-medium pointer-events-none text-gray-100 z-10">
            {isVideo ? 'Video' : `${image.height}×${image.width}`}
          </span>
        </button>
      )}

      {/* Image name */}
      {displayName && (
        <div>
          {image.name} {originalLabel({ original: original })} {isVideo && '(Video)'}
        </div>
      )}
    </div>
  )
}
